import sparkleIcon from '@/assets/copilot/sparkle.svg'
import { XMarkIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import PanelHeader from '../PanelHeader'

type CopilotHeaderProps = {
  onClose: () => void
}

/**
 * Copilot's bar, wearing the same `PanelHeader` as every other column in the
 * live screen (BRAI-681).
 *
 * It used to be its own near-miss of that bar: white where the others are
 * tinted, a hand-set 51px tall against their padding, `pr-[13.99px]`. Each
 * difference was small and none of them was visible until the panel opened
 * next to the columns it was supposed to match.
 *
 * The marque stays, though. The columns draw a bare sparkle in the accent
 * colour; this is the product's own assistant rather than one more pane of
 * the transcript, and the filled tile is how it has always said so. What was
 * wrong was the bar around it, not the badge.
 *
 * Closing rather than folding is the other thing kept: this panel is opened
 * from the control bar and closed again, where the others fold away and leave
 * a strip behind. The control sits where their fold control does and is built
 * from the same pieces.
 */
export default function CopilotHeader({ onClose }: CopilotHeaderProps) {
  const { t } = useUiStrings()

  return (
    <PanelHeader
      icon={
        <span
          data-testid="copilot-header-badge"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#2563eb] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]"
        >
          <img
            src={sparkleIcon}
            alt=""
            width={14}
            height={14}
            className="block h-[14px] w-[14px]"
          />
        </span>
      }
      title={t.copilotTitle}
      actions={
        <>
          <p className="flex shrink-0 items-center gap-1 text-[11px] font-medium leading-none text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden="true" />
            {t.copilotListening}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.copilotCloseAria}
            title={t.copilotCloseAria}
            data-testid="copilot-close-button"
            className="flex h-7 w-7 items-center justify-center rounded-md text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700 dark:text-[var(--accent)] dark:hover:bg-[var(--accent-light)] dark:hover:text-[var(--accent-hover)]"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </>
      }
    />
  )
}
